import React, { FC, useEffect, useMemo, useRef, useState } from "react";

import { ServiceInterface } from "./service.interface";

import {
  GeoService,
  FileService,
  LoggerService,
  ApiService,
  AuthService,
  PermissionService,
  DeviceService,
  UserService,
  MessageService,
  PushService,
  StorageService,
  DivisionService,
  EventService,
  GameService,
  SessionService,
  ReachabilityService,
  I18nService,
  PreferenceService,
  StorageAdapterInterface,
  FSAdapterInterface,
  GeolocationAdapterInterface,
  PermissionAdapterInterface,
} from "./services.barrel";

/* Instantiate services */

import { PushAdapterInterface } from "./push";
import { NetInfoAdapterInterface } from "./reachability";
import { Loadable } from "../loadable";
import { AppStateAdapter, AppStateStatus } from "./app-state-adapter";
import { LoginAdapterInterface } from "./user";
import { DeviceAdapterInterface } from "./device/device-adapter.interface";
import { Deferred } from "@citychallenge/common";

export interface ServiceAdapters {
  appState: AppStateAdapter;
  device: DeviceAdapterInterface;
  fs: FSAdapterInterface;
  geolocation: GeolocationAdapterInterface;
  login: LoginAdapterInterface;
  netinfo: NetInfoAdapterInterface;
  permission: PermissionAdapterInterface;
  push: PushAdapterInterface;
  storage: StorageAdapterInterface;
  secureStorage: StorageAdapterInterface;
}

export interface ServiceProviderProps {
  servicesReady?: () => void;
  adapters: ServiceAdapters;
}

export type ProviderMap = {
  provide: React.Context<any>;
  service: Partial<ServiceInterface>;
};

export const useServiceProvider = (
  adapters: ServiceAdapters,
  servicesReady?: () => void
) => {
  const [ready, setReady] = useState(false);

  const services = useMemo<ServiceInterface[]>(() => {
    const loggerService = new LoggerService();
    const deviceService = new DeviceService(adapters.device);
    const messageService = new MessageService();
    const storageService = new StorageService(
      adapters.storage,
      adapters.secureStorage,
      loggerService
    );
    const preferenceService = new PreferenceService(storageService);
    const authService = new AuthService(storageService);
    const apiService = new ApiService(
      authService,
      storageService,
      deviceService
    );
    const permissionService = new PermissionService(
      storageService,
      adapters.permission
    );
    const fileService = new FileService(apiService, adapters.fs);

    const userService = new UserService(
      loggerService,
      apiService,
      authService,
      messageService,
      storageService,
      adapters.login
    );
    const geoService = new GeoService(
      apiService,
      storageService,
      permissionService,
      adapters.geolocation,
      adapters.appState
    );
    const pushService = new PushService(
      permissionService,
      userService,
      adapters.push
    );

    const gameService = new GameService(
      apiService,
      fileService,
      authService,
      deviceService
    );

    const reachabilityService = new ReachabilityService(adapters.netinfo);
    const i18nService = new I18nService(
      apiService,
      preferenceService,
      userService,
      deviceService
    );

    const sessionService = new SessionService(
      loggerService,
      apiService,
      reachabilityService,
      storageService,
      userService,
      i18nService,
      deviceService,
      fileService,
      gameService
    );

    const divisionService = new DivisionService(
      apiService,
      userService,
      storageService,
      messageService,
      sessionService,
      i18nService,
      deviceService
    );

    const eventService = new EventService(
      apiService,
      userService,
      divisionService
    );

    return [
      loggerService,
      deviceService,
      messageService,
      storageService,
      preferenceService,
      authService,
      apiService,
      permissionService,
      fileService,
      userService,
      geoService,
      pushService,
      gameService,
      reachabilityService,
      i18nService,
      sessionService,
      divisionService,
      eventService,
    ];
  }, []);

  // When app starts we are not in foreground
  const isForeground = useRef(false);

  function foregroundChanged(foreground: boolean) {
    for (const service of services) {
      if (foreground) {
        service.serviceDidBecomeForeground &&
          service.serviceDidBecomeForeground();
      } else {
        service.serviceDidBecomeBackground &&
          service.serviceDidBecomeBackground();
      }
    }
  }

  function switchAppState(appState: AppStateStatus) {
    const current = appState === "active";
    if (isForeground.current === current) return;
    isForeground.current = current;
    foregroundChanged(current);
  }

  const [deferred] = useState(() => new Deferred());
  const [loadable, setLoadable] = useState<Loadable<any>>();

  useEffect(() => {
    const promises: Promise<any>[] = [];

    setReady(false);

    for (const service of services) {
      const result = service.serviceWillMount && service.serviceWillMount();

      if (result instanceof Promise) {
        promises.push(result);
      }
    }

    adapters.appState.addEventListener("change", switchAppState);

    const servicesPromise = Promise.all(promises);

    servicesPromise
      .then(() => {
        servicesReady?.();
        setReady(true);
        deferred.resolve();

        for (const service of services) {
          service.serviceWillBoot?.();
        }
        switchAppState(adapters.appState.currentState);
      })
      .catch(() => {
        deferred.reject();
      });

    setLoadable(Loadable.create(() => servicesPromise));

    return () => {
      for (const service of services) {
        service.serviceWillUnmount && service.serviceWillUnmount();
      }
      adapters.appState.removeEventListener("change", switchAppState);
    };
  }, []);

  return { loadable, services, ready, promise: deferred.promise };
};

const ServiceReducer: FC<{ services: ServiceInterface[] }> = ({
  services,
  children,
}) => {
  return (
    <>
      {services.reduceRight(
        (prev, service) => (
          <service.context.Provider value={service}>
            {prev}
          </service.context.Provider>
        ),
        children
      )}
    </>
  );
};

/**
 * Service provider that renders it's children in combination with a loadable
 */

export const ServiceProvider: FC<
  ServiceProviderProps & {
    children: (loadable: Loadable<any>) => React.ReactNode;
  }
> = ({ adapters, servicesReady, children }) => {
  const { loadable, services } = useServiceProvider(adapters, servicesReady);
  return (
    <ServiceReducer services={services}>
      {loadable ? children(loadable) : null}
    </ServiceReducer>
  );
};

export const ServiceSuspenseHelper: FC<
  ReturnType<typeof useServiceProvider>
> = ({ ready, services, promise, children }) => {
  if (!ready) throw promise;

  return <ServiceReducer services={services}>{children}</ServiceReducer>;
};

/**
 * Services provider that is compatible with react suspense
 */
export const LazyServiceProvider: FC<ServiceProviderProps> = ({
  adapters,
  servicesReady,
  children,
}) => {
  const serviceValues = useServiceProvider(adapters, servicesReady);

  return (
    <ServiceSuspenseHelper {...serviceValues}>{children}</ServiceSuspenseHelper>
  );
};
